import React from 'react'
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  Outlet,
} from 'react-router-dom'
import { ApicProvider } from '../../gen/client/react'
import { AuthProvider } from './auth/AuthContext'
import { Callback } from './auth/Callback'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { ROUTE_PERMISSIONS } from './auth/routePermissions'
import { AdminLayout } from './layout/AdminLayout'
import { BareLayout } from './layout/BareLayout'
import { TenantProvider } from './layout/TenantSwitcher'
import { Docs } from './routes/Docs'
import { Tenants } from './pages/Tenants'
import { Users } from './pages/Users'
import { Connectors } from './pages/Connectors'
import { TenantSecretsProvider } from './pages/TenantSecretsProvider'
import { TenantMailProvider } from './pages/TenantMailProvider'
import { Clients } from './pages/Clients'
import { Audit } from './pages/Audit'
import { MtlsCAs } from './pages/MtlsCAs'
import { Subjects } from './pages/Subjects'
import { Resources } from './pages/Resources'
import { Apps } from './pages/Apps'
import { Entitlements } from './pages/Entitlements'
import { Policies } from './pages/Policies'
import StepUpPolicy from './pages/StepUpPolicy'
import { AccessRequests } from './pages/AccessRequests'
import { AccessRequestDetail } from './pages/AccessRequestDetail'
import { ActivateGrant } from './pages/ActivateGrant'
import { EvidenceView } from './pages/EvidenceView'
import { BreakGlassConsole } from './pages/BreakGlassConsole'
import { EmergencyRoles } from './pages/EmergencyRoles'
import { AuditEvidence } from './pages/AuditEvidence'
import { AuditEvidenceList } from './pages/AuditEvidenceList'
import { AuditChainIntegrityPage } from './pages/AuditChainIntegrity'
import { WebAuthnCredentials } from './pages/WebAuthnCredentials'
import { Profile } from './pages/Profile'
import { UserFactors } from './pages/UserFactors'
import { BrokeredSessionsPage } from './pages/BrokeredSessions/BrokeredSessionsPage'
import { BrokeredSessionDetailPage } from './pages/BrokeredSessions/BrokeredSessionDetailPage'
import { SessionControlPolicyPage } from './pages/BrokerConfig/SessionControlPolicyPage'
import { EphemeralCredsPage } from './pages/EphemeralCreds'
import { CAEDashboard } from './pages/CAEDashboard'
import { ResilienceDashboardPage } from './pages/ResilienceDashboard'
import { GovernanceDiscovery } from './pages/GovernanceDiscovery'
import { GovernanceCampaign } from './pages/GovernanceCampaign'
import { GovernanceRoles } from './pages/GovernanceRoles'
import { GovernanceDualAuth } from './pages/GovernanceDualAuth'
import { GovernanceRoleRegistry } from './pages/GovernanceRoleRegistry'
import { PamOverview } from './pages/PamOverview'
import { PamPasskeyGate } from './auth/PamPasskeyGate'
import { client } from './api/http'
import { SetupRoute } from './pages/setup/SetupRoute'
import { FirstRunRedirect } from './pages/setup/FirstRunRedirect'

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

const App = () => (
  <BrowserRouter>
    <ApicProvider value={client}>
      <AuthProvider>
        <TenantProvider>
          <Routes>
            {/* PUBLIC — chooses the setup-token wizard for interactive mode or
                the standard /admin sign-in path for Terraform mode. Kept
                outside FirstRunRedirect because it is that gate's interactive
                destination. */}
            <Route path="/setup" element={<SetupRoute />} />
            {/* Everything else is behind the mode-aware first-run gate. */}
            <Route
              element={(
                <FirstRunRedirect>
                  <Outlet />
                </FirstRunRedirect>
              )}
            >
              <Route path="/" element={<Docs />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/admin/callback" element={<Callback />} />
              <Route
                path="/admin/activate/:requestId"
                element={(
                  <ProtectedRoute>
                    <BareLayout>
                      <ActivateGrant />
                    </BareLayout>
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/admin"
                element={(
                  <ProtectedRoute requireAdmin>
                    <AdminLayout />
                  </ProtectedRoute>
                )}
              >
                <Route index element={<Navigate to="pam-overview" replace />} />
                <Route path="tenants" element={<Tenants />} />
                <Route path="users" element={<Users />} />
                {/* Task 11: the genuine ADMIN per-user factor view (distinct
                    from /admin/security-keys' self-service, own-passkeys-only
                    surface). CRITICAL-1 ledger item (fix-wave 2026-07-24):
                    now gated by PermTenantUsersFactorsManage
                    (tenant:users.factors_manage, pam_permissions.go), the
                    permission admin_user_factors.go's handlers actually
                    enforce — closing the "nav-hidden but reachable by direct
                    URL" gap this route previously had. */}
                <Route
                  path="users/:userId/factors"
                  element={(
                    <ProtectedRoute
                      requireAdmin
                      requiredPermission={ROUTE_PERMISSIONS['/admin/users/:userId/factors']}
                    >
                      <UserFactors />
                    </ProtectedRoute>
                  )}
                />
                <Route path="connectors" element={<Connectors />} />
                <Route
                  path="secrets-provider"
                  element={(
                    <ProtectedRoute
                      requireAdmin
                      requiredPermission={ROUTE_PERMISSIONS['/admin/secrets-provider']}
                    >
                      <TenantSecretsProvider />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="mail-provider"
                  element={(
                    <ProtectedRoute
                      requireAdmin
                      requiredPermission={ROUTE_PERMISSIONS['/admin/mail-provider']}
                    >
                      <TenantMailProvider />
                    </ProtectedRoute>
                  )}
                />
                <Route path="clients" element={<Clients />} />
                <Route
                  path="audit"
                  element={(
                    <ProtectedRoute
                      requireAdmin
                      requiredPermission={ROUTE_PERMISSIONS['/admin/audit']}
                    >
                      <Audit />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="audit-chain-integrity"
                  element={(
                    <ProtectedRoute
                      requireAdmin
                      requiredPermission={
                        ROUTE_PERMISSIONS['/admin/audit-chain-integrity']
                      }
                    >
                      <AuditChainIntegrityPage />
                    </ProtectedRoute>
                  )}
                />
                <Route path="mtls-cas" element={<MtlsCAs />} />
                <Route path="subjects" element={<Subjects />} />
                <Route path="resources" element={<Resources />} />
                <Route path="apps" element={<Apps />} />
                <Route path="entitlements" element={<Entitlements />} />
                <Route path="api-docs" element={<Docs />} />
                {/* security-keys stays a direct child (outside PamPasskeyGate):
                    a user without a passkey must still be able to reach the
                    registration page directly, e.g. via the "Passkeys" nav
                    link, without the gate itself blocking it. */}
                <Route path="security-keys" element={<WebAuthnCredentials />} />
                {/* Task 10: self-service profile/emails/app-passwords — a
                    direct child (outside PamPasskeyGate) like security-keys
                    above, since a user must be able to manage their own
                    account regardless of PAM passkey-enrollment status. */}
                <Route path="me" element={<Profile />} />
                <Route element={<PamPasskeyGate />}>
                  <Route path="pam-overview" element={<PamOverview />} />
                  <Route
                    path="policies"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/policies']}
                      >
                        <Policies />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="stepup-policy"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/stepup-policy']}
                      >
                        <StepUpPolicy />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="access-requests"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/access-requests']}
                      >
                        <AccessRequests />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="access-requests/:requestId"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/access-requests/:requestId']}
                      >
                        <AccessRequestDetail />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="access-requests/:requestId/evidence"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/access-requests/:requestId/evidence']}
                      >
                        <EvidenceView />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="break-glass"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/break-glass']}
                      >
                        <BreakGlassConsole />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="emergency-roles"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/emergency-roles']}
                      >
                        <EmergencyRoles />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="audit-evidence"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/audit-evidence']}
                      >
                        <AuditEvidenceList />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="audit-evidence/:requestId"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/audit-evidence/:requestId']}
                      >
                        <AuditEvidence />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="brokered-sessions"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/brokered-sessions']}
                      >
                        <BrokeredSessionsPage />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="brokered-sessions/:sessionId"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/brokered-sessions/:sessionId']}
                      >
                        <BrokeredSessionDetailPage />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="session-control-policy"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/session-control-policy']}
                      >
                        <SessionControlPolicyPage />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="ephemeral-creds"
                    element={<EphemeralCredsPage />}
                  />
                  <Route path="cae" element={<CAEDashboard />} />
                  <Route
                    path="resilience"
                    element={<ResilienceDashboardPage />}
                  />
                  <Route
                    path="governance/discovery"
                    element={<GovernanceDiscovery />}
                  />
                  <Route
                    path="governance/campaigns"
                    element={<GovernanceCampaign />}
                  />
                  <Route
                    path="governance/roles"
                    element={<GovernanceRoles />}
                  />
                  <Route
                    path="governance/role-registry"
                    element={(
                      <ProtectedRoute
                        requireAdmin
                        requiredPermission={ROUTE_PERMISSIONS['/admin/governance/role-registry']}
                      >
                        <GovernanceRoleRegistry />
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="governance/dual-auth"
                    element={<GovernanceDualAuth />}
                  />
                </Route>
              </Route>
            </Route>
          </Routes>
        </TenantProvider>
      </AuthProvider>
    </ApicProvider>
  </BrowserRouter>
)

export default App
